// Sending something for signature, from a browser. spec/04 §2, §3.
//
// Two phases, because the middle of them needs the document on screen.
//
//   1. Choose a file and who signs it. The bytes go to storage, the envelope is
//      created and prepared.
//   2. Place a signature field for each signer by clicking the page, then send.
//
// The split is not cosmetic: an envelope exists as a DRAFT between the two, so
// a sender who closes the tab has not lost the upload, and nothing is asked of
// a signer until somebody has actually placed the fields.

import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  ApiError,
  addFields,
  createEnvelope,
  createUpload,
  distributeEnvelope,
  listEnvelopes,
  prepareEnvelope,
  putUpload,
  type EnvelopeDetail,
  type EnvelopeSummary,
} from './api.ts';
import type { PlacedField } from './FieldPlacer.tsx';
import { readSession } from './session.ts';

// Loaded only when a sender reaches the placement step. pdf.js is most of this
// app's weight, and a signer opening a link on a phone must not download a
// renderer they never use to read a document their browser renders itself.
const FieldPlacer = lazy(() =>
  import('./FieldPlacer.tsx').then((module) => ({ default: module.FieldPlacer })),
);

type Outcome = { kind: 'ok'; message: string } | { kind: 'bad'; message: string } | null;

interface Drafted {
  readonly envelope: EnvelopeDetail;
  readonly file: File;
  readonly deliver: 'EMAIL' | 'NONE';
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Something between this page and the API answered instead.';
}

export function EnvelopesPage(): ReactElement {
  const session = readSession();
  const token = session?.token ?? '';

  const [envelopes, setEnvelopes] = useState<EnvelopeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [drafted, setDrafted] = useState<Drafted | null>(null);
  const [fields, setFields] = useState<readonly PlacedField[]>([]);

  const refresh = useCallback(async () => {
    setEnvelopes((await listEnvelopes(token)).data);
  }, [token]);

  useEffect(() => {
    if (session === null) {
      window.location.assign('/login');
      return;
    }
    refresh()
      .catch((error: unknown) => setOutcome({ kind: 'bad', message: messageOf(error) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  if (session === null) return <main className="wrap" />;

  async function upload(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = form.get('document');
    const title = String(form.get('title') ?? '').trim();
    const deliver = String(form.get('deliver') ?? 'EMAIL') as 'EMAIL' | 'NONE';

    // Blank rows are the normal way to use a fixed set of inputs for "up to
    // three signers", so they are dropped rather than refused.
    const signers = [0, 1, 2]
      .map((index) => ({
        name: String(form.get(`signerName${index}`) ?? '').trim(),
        email: String(form.get(`signerEmail${index}`) ?? '').trim(),
      }))
      .filter((signer) => signer.name !== '' || signer.email !== '');

    if (!(file instanceof File) || file.size === 0) {
      setOutcome({ kind: 'bad', message: 'Choose a PDF to send.' });
      return;
    }
    if (signers.length === 0) {
      setOutcome({ kind: 'bad', message: 'Add at least one signer.' });
      return;
    }
    if (deliver === 'EMAIL' && signers.some((signer) => signer.email === '')) {
      setOutcome({ kind: 'bad', message: 'Every signer needs an address to send by email.' });
      return;
    }

    setOutcome(null);
    setBusy(true);
    try {
      // Each step names itself. This is four requests and one of them is a
      // file upload; a spinner that says nothing leaves a sender guessing
      // which part is slow.
      setStep('Asking for an upload URL…');
      const ticket = await createUpload(token, {
        filename: file.name,
        mimeType: file.type === '' ? 'application/pdf' : file.type,
        byteLength: file.size,
      });

      setStep('Uploading the document…');
      await putUpload(ticket, file);

      setStep('Creating the envelope…');
      const envelope = await createEnvelope(token, {
        title: title === '' ? file.name : title,
        items: [{ filename: file.name, uploadId: ticket.uploadId }],
        parties: signers.map((signer) => ({
          role: 'SIGNER',
          ...(signer.name === '' ? {} : { name: signer.name }),
          ...(signer.email === '' ? {} : { email: signer.email }),
        })),
      });

      setStep('Preparing it for signature…');
      await prepareEnvelope(token, envelope.id);

      setDrafted({ envelope, file, deliver });
      setFields([]);
      await refresh();
    } catch (error) {
      setOutcome({ kind: 'bad', message: messageOf(error) });
    } finally {
      setBusy(false);
      setStep(null);
    }
  }

  async function send(): Promise<void> {
    if (drafted === null) return;
    const signers = drafted.envelope.parties.filter((party) => party.role === 'SIGNER');
    const item = drafted.envelope.items[0];
    if (item === undefined) return;

    // A signature each, specifically. A signer with only a text box has
    // nothing to sign with, and the API refuses the envelope for that reason
    // — finding out afterwards means recalling something people have opened.
    const without = signers.filter(
      (signer) =>
        !fields.some((field) => field.partyKey === signer.id && field.type === 'SIGNATURE'),
    );
    if (without.length > 0) {
      // Refused here as a courtesy; the API refuses it too. A signer with no
      // field is an envelope that cannot be completed, and finding that out
      // after sending means recalling something people have already opened.
      setOutcome({
        kind: 'bad',
        message: `Place a signature for ${without
          .map((signer) => signer.name ?? signer.email ?? 'a signer')
          .join(', ')} before sending.`,
      });
      return;
    }

    setOutcome(null);
    setBusy(true);
    try {
      setStep('Placing the fields…');
      await addFields(
        token,
        drafted.envelope.id,
        fields.map((field) => ({
          type: field.type,
          partyId: field.partyKey,
          itemId: item.id,
          page: field.page,
          x: field.x,
          y: field.y,
          w: field.w,
          h: field.h,
        })),
      );

      setStep('Sending…');
      const sent = await distributeEnvelope(token, drafted.envelope.id, {
        method: drafted.deliver,
      });

      setDrafted(null);
      setFields([]);
      await refresh();
      setOutcome({
        kind: 'ok',
        message:
          drafted.deliver === 'EMAIL'
            ? 'Sent. The invitation is queued; the signing link is minted when it is emailed, so it is deliberately not shown here.'
            : `Ready. Signing links: ${(sent.links ?? []).map((link) => link.url).join(' · ')}`,
      });
    } catch (error) {
      setOutcome({ kind: 'bad', message: messageOf(error) });
    } finally {
      setBusy(false);
      setStep(null);
    }
  }

  const banner =
    outcome === null ? null : (
      <div className={outcome.kind === 'ok' ? 'notice' : 'error'} role="alert">
        <p>{outcome.message}</p>
      </div>
    );

  return (
    <main className="wrap">
      <h1>Envelopes</h1>
      <p className="muted">
        Signed in as {session.email} · <a href="/admin">Settings</a>
      </p>

      {drafted === null ? (
        <section className="setting" aria-labelledby="send-heading">
          <h2 id="send-heading">Send a document</h2>
          <p className="muted">
            Uploads need object storage configured in <a href="/admin">Settings</a>; sending
            by email needs mail configured there too.
          </p>

          <form onSubmit={upload} className="assent" aria-labelledby="send-heading">
            <p>
              <label htmlFor="document">Document (PDF)</label>
              <input id="document" name="document" type="file" accept="application/pdf" required />
            </p>
            <p>
              <label htmlFor="title">Title</label>
              <input id="title" name="title" type="text" placeholder="The file name, if left blank" />
            </p>

            {[0, 1, 2].map((index) => (
              <fieldset key={index} className="signer">
                <legend>{index === 0 ? 'Signer' : `Signer ${index + 1} (optional)`}</legend>
                <p>
                  <label htmlFor={`signerName${index}`}>Name</label>
                  <input id={`signerName${index}`} name={`signerName${index}`} type="text" />
                </p>
                <p>
                  <label htmlFor={`signerEmail${index}`}>Email</label>
                  <input id={`signerEmail${index}`} name={`signerEmail${index}`} type="email" />
                </p>
              </fieldset>
            ))}

            <p>
              <label htmlFor="deliver">Delivery</label>
              <select id="deliver" name="deliver" defaultValue="EMAIL">
                <option value="EMAIL">Email each signer</option>
                <option value="NONE">Give me the links instead</option>
              </select>
            </p>

            {banner}

            <div className="actions">
              <button type="submit" disabled={busy}>
                {busy ? (step ?? 'Working…') : 'Upload and place fields'}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="setting" aria-labelledby="place-heading">
          <h2 id="place-heading">Place the signatures — {drafted.envelope.title}</h2>
          <p className="muted">
            This envelope is a draft until you send it. Nothing has been asked of anybody yet.
          </p>

          <Suspense fallback={<p className="muted">Loading the page viewer…</p>}>
            <FieldPlacer
            file={drafted.file}
            parties={drafted.envelope.parties
              .filter((party) => party.role === 'SIGNER')
              .map((party) => ({
                key: party.id,
                label: party.name ?? party.email ?? 'Signer',
              }))}
            fields={fields}
            onChange={setFields}
            />
          </Suspense>

          {banner}

          <div className="actions">
            <button type="button" onClick={() => void send()} disabled={busy}>
              {busy ? (step ?? 'Working…') : `Send (${fields.length} field${fields.length === 1 ? '' : 's'})`}
            </button>
            <button
              type="button"
              className="linklike"
              onClick={() => {
                setDrafted(null);
                setFields([]);
                setOutcome({
                  kind: 'ok',
                  message: 'Left as a draft. The upload is kept; nothing was sent.',
                });
              }}
              disabled={busy}
            >
              Leave as a draft
            </button>
          </div>
        </section>
      )}

      <section className="setting" aria-labelledby="list-heading">
        <h2 id="list-heading">Recent</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : envelopes.length === 0 ? (
          <p className="muted">Nothing sent yet.</p>
        ) : (
          <table className="envelopes">
            <caption className="muted">The most recent envelopes on this instance.</caption>
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Status</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {envelopes.map((envelope) => (
                <tr key={envelope.id}>
                  <td>
                    <a href={`/envelopes/${envelope.id}`}>{envelope.title}</a>
                  </td>
                  <td>{envelope.status}</td>
                  <td>{new Date(envelope.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
